"use client";

import { useState } from "react";
import type {
  DeleteTopicsRequest,
  GenerateQuestionsRequest,
  ProposePostRequest,
  PutTopicRequest,
} from "@/generated/fastapi";
import { useAuth } from "@/state/globalStore";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback";
import type { UpdateTopicInput } from "./useIdentifiedTopicsState";
import { deleteTopics } from "../../api/deleteTopics";
import { generateQuestions } from "../../api/generateQuestions";
import { proposePost } from "../../api/proposePost";
import { putTopic } from "../../api/putTopic";

export default function useTopicReviewActions({
  updateTopic,
  setProposedPost,
}: {
  updateTopic: (topicId: string, update: UpdateTopicInput) => void;
  setProposedPost?: (post: string | null) => void;
}) {
  const { accessToken } = useAuth();

  const [isGeneratingQuestions, setIsGeneratingQuestions] =
    useState<boolean>(false);
  const [isProposingPost, setIsProposingPost] = useState<boolean>(false);

  async function handleConfirmTopicClick(
    request: PutTopicRequest,
    onSuccess?: () => void,
  ) {
    const response = await putTopic(accessToken, request);
    if (response) {
      showSuccessToast("Topic confirmed successfully");
      updateTopic(request.topic_id, {
        confirmed_at: response.confirmed_at || new Date().toISOString(),
      });
      onSuccess?.();
    } else {
      showErrorToast("Failed to confirm topic");
    }
  }

  async function handleDeleteTopicClick(
    request: DeleteTopicsRequest,
    onSuccess?: () => void,
  ) {
    const response = await deleteTopics(accessToken, request);
    if (response) {
      showSuccessToast("Topic deleted successfully");
      onSuccess?.();
    } else {
      showErrorToast("Failed to delete topic");
    }
  }

  async function handleGenerateQuestionsClick(
    request: GenerateQuestionsRequest,
    onSuccess?: () => void,
  ) {
    setIsGeneratingQuestions(true);
    const response = await generateQuestions(accessToken, request);
    if (
      response &&
      response.prompting_questions &&
      response.prompting_questions.length > 0
    ) {
      showSuccessToast(
        `${response.prompting_questions.length} questions regenerated successfully`,
      );
      updateTopic(request.topic_id, {
        prompting_questions: response.prompting_questions,
      });
      onSuccess?.();
    } else if (
      response &&
      response.prompting_questions &&
      response.prompting_questions.length === 0
    ) {
      showWarningToast("No questions were generated. Try again");
      updateTopic(request.topic_id, {
        prompting_questions: [],
      });
      onSuccess?.();
    } else {
      showErrorToast("Failed to regenerate questions");
    }

    setIsGeneratingQuestions(false);
  }

  async function handleProposePostClick(
    request: ProposePostRequest,
    onSuccess?: () => void,
  ) {
    setIsProposingPost(true);
    const response = await proposePost(accessToken, request);
    if (response && response.status === "success") {
      showSuccessToast("Post proposed successfully");
      if (setProposedPost) {
        setProposedPost(response.proposed_post || null);
      }
      if (onSuccess) {
        onSuccess();
      }
    } else {
      showErrorToast("Failed to propose post");
    }
    setIsProposingPost(false);
  }

  return {
    handleConfirmTopicClick,
    handleDeleteTopicClick,
    handleGenerateQuestionsClick,
    isGeneratingQuestions,
    handleProposePostClick,
    isProposingPost,
  };
}
